/**
 * Pure helpers for AiAssistantDrawer — extracted for unit testing.
 */
import type { BuilderSurface } from "@/lib/ai/ai-store";

export type ScrollMetrics = {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
};

/**
 * Tolerance for "pinned to bottom": sub-pixel scroll positions, border
 * rounding and momentum overshoot mean an exact == 0 check would unpin
 * the auto-scroll on almost every streamed chunk.
 */
export const BOTTOM_PIN_THRESHOLD_PX = 48;

/** True when the scroll position counts as "at the bottom" (keep auto-scrolling). */
export function isNearBottom(
  metrics: ScrollMetrics,
  threshold: number = BOTTOM_PIN_THRESHOLD_PX,
): boolean {
  return metrics.scrollHeight - (metrics.scrollTop + metrics.clientHeight) <= threshold;
}

/**
 * Starter-prompt i18n keys (under aiAssistant.starters) for the empty
 * state. A null scope can't happen while the drawer is mounted (the
 * provider gates on scope !== null) — fall back to the event set.
 */
export function getStarterKeys(scopeKind: "event" | "company" | null): string[] {
  const base = scopeKind === "company" ? "company" : "event";
  return [`${base}1`, `${base}2`, `${base}3`];
}

/**
 * Starter-prompt i18n keys for the in-builder empty state, per builder surface
 * (under aiAssistant.builder.starters.<surface>). Only the block-canvas builders
 * (email/document/website) ship starters; the flat-config builders return none.
 */
const BUILDER_STARTER_SURFACES = new Set<BuilderSurface>(["email", "document", "website"]);

export function getBuilderStarterKeys(surface: BuilderSurface | null): string[] {
  if (!surface || !BUILDER_STARTER_SURFACES.has(surface)) return [];
  return [1, 2, 3].map((n) => `builder.starters.${surface}.${n}`);
}
